import { colors, glassCard, radius, typography, cardBlur } from '../../theme';
import { Box, CssBaseline, Typography, Skeleton } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where, Timestamp, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { FaStar, FaPhoneAlt } from 'react-icons/fa';
import { TiLocation } from 'react-icons/ti';
import { MdTimer } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import fallbackShopImage from '../../images/Shop Icon (SVG).svg';
import fallbackBarberImage from '../../images/Barber Icon (SVG).svg';
import barberFallback from '../../images/barber-fallback.png';
import { ArrowBack, ArrowForwardIos, Star } from '@mui/icons-material';
import MyQueue from './MyQueue';
import { BookingCard } from './UserBookings';
import { onAuthStateChanged } from 'firebase/auth';

// ─── helpers ──────────────────────────────────────────────────────────────────
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function formatHoursLabel(hours) {
    if (!hours) return 'Off today';
    return `${formatTo12(hours.open)} – ${formatTo12(hours.close)}`;
}


function getTodayHours(barber, shop) {
    const dayKey = DAY_KEYS[new Date().getDay()];
    const hours = barber.operatingHours?.[dayKey] ?? shop?.operatingHours?.[dayKey];
    if (!hours || hours.isOff) return null;
    return hours;
}

const isOpenNow = (shop) => {
    const now = new Date();
    const hours = shop.operatingHours?.[DAY_KEYS[now.getDay()]];
    if (!hours || hours.isOff) return false;
    const [oh, om] = hours.open.split(':').map(Number);
    const [ch, cm] = hours.close.split(':').map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return nowMins >= oh * 60 + om && nowMins < ch * 60 + cm;
};

const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatTo12 = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const getTodayHoursLabel = (shop) => {
    const hours = shop.operatingHours?.[DAY_KEYS[new Date().getDay()]];
    if (!hours || hours.isOff) return null;
    return `${formatTo12(hours.open)} – ${formatTo12(hours.close)}`;
};

const isSubscriptionActive = (shop) => {
    const plan = shop.plan ?? 'free';
    if (plan === 'free') return true;
    const paidUntil = shop.paidUntil?.toDate?.() ?? (shop.paidUntil ? new Date(shop.paidUntil) : null);
    if (!paidUntil) return false;
    return paidUntil > new Date();
};

const getShopRating = (shop) => shop.rating ?? shop.ratings?.average ?? 0;
const getShopReviewCount = (shop) => shop.ratingCount ?? shop.ratings?.totalReviews ?? 0;

const TABS = ['Shops', 'Barbers', 'My Queue'];

const UserBookings = () => {
    const navigate = useNavigate();
    const [bookings,  setBookings]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [userId,    setUserId]    = useState(null);
    const [activeTab, setActiveTab] = useState('upcoming');

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) { navigate('/signin'); return; } setUserId(user.uid); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, 'bookings'), where('userId', '==', userId));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.scheduledAt?.toDate?.() ?? new Date(0)) - (a.scheduledAt?.toDate?.() ?? new Date(0)));
            setBookings(data); setLoading(false);
        }, (err) => { console.error(err); setLoading(false); });
        return () => unsub();
    }, [userId]);

    const today    = new Date(); today.setHours(0,0,0,0);
    const upcoming = bookings.filter(b => { const d = b.scheduledAt?.toDate?.() ?? new Date(0); return d >= today && b.status !== 'cancelled' && b.status !== 'completed'; });
    const past     = bookings.filter(b => { const d = b.scheduledAt?.toDate?.() ?? new Date(0); return d < today || b.status === 'completed' || b.status === 'cancelled'; });
    const handleOpenTicket = (booking) => navigate(`/queue/${booking.shopId}/${booking.id}`);
    const displayList = activeTab === 'upcoming' ? upcoming : past;

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight:'100vh', color: colors.textPrimary, pb:8 }}>


            <Box sx={{ display:'flex', px:3, mt:2.5, gap:1 }}>
                {[{ key:'upcoming', label:`Upcoming (${upcoming.length})` }, { key:'past', label:`Past (${past.length})` }].map(tab => (
                    <Box key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{ px:2, py:0.8, borderRadius: radius.md, cursor:'pointer', bgcolor: activeTab === tab.key ? colors.primary : colors.bgCard, border:`1px solid ${activeTab === tab.key ? colors.primary : colors.border}`, transition:'all .2s' }}>
                        <Typography sx={{ fontSize:12, fontWeight:600, color: activeTab === tab.key ? colors.bg : colors.textMuted }}>{tab.label}</Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ px:3, mt:2.5, display:'flex', flexDirection:'column', gap:2 }}>
                {loading ? (
                    [1,2,3].map(i => <Box key={i} sx={{ bgcolor: colors.bgCard, borderRadius: radius.lg, height:140, opacity:0.4 }} />)
                ) : displayList.length === 0 ? (
                    <Box sx={{ textAlign:'center', py:8 }}>
                        <Typography sx={{ fontSize:36, mb:2 }}>{activeTab === 'upcoming' ? '📅' : '✂️'}</Typography>
                        <Typography sx={{ fontSize:16, color: colors.textFaint }}>{activeTab === 'upcoming' ? 'No upcoming bookings' : 'No past bookings'}</Typography>
                        {activeTab === 'upcoming' && <Typography onClick={() => navigate('/user/dashboard')} sx={{ fontSize:13, color: colors.primary, mt:1, cursor:'pointer', '&:hover':{ textDecoration:'underline' } }}>Find a barber →</Typography>}
                    </Box>
                ) : displayList.map(booking => <BookingCard key={booking.id} booking={booking} onOpenTicket={handleOpenTicket} />)}
            </Box>
        </Box>
    );
};

// ─── Shop Grid Card ───────────────────────────────────────────────────────────
const ShopGridCard = ({ shop, onClick }) => {
    const openNow = isOpenNow(shop);
    const todayHours = getTodayHoursLabel(shop);
    const rating = getShopRating(shop);
    const reviewCount = getShopReviewCount(shop);

    return (
        <Box onClick={onClick} sx={{
            borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
            transition: 'transform .2s, box-shadow .2s',
            bgcolor:colors.bgGradient,
         
            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${colors.shadow}` },
        }}>
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden' }}>
                <Box component="img" src={shop.image || fallbackShopImage} alt={shop.shopName}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: openNow ? colors.successBg : colors.errorBg, border: `1px solid ${openNow ? colors.successBorder : colors.errorBorder}`, backdropFilter: 'blur(8px)', borderRadius: radius.sm, px: 0.7, py: 0.15 }}>
                    <Typography sx={{ fontSize: 8, fontWeight: 700, color: openNow ? colors.success : colors.error }}>
                        {openNow ? '● OPEN' : '● CLOSED'}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ p: 1.2 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 13, fontWeight: 700, lineHeight: 1.2, mb: 0.4, color: colors.textPrimary }} noWrap>{shop.shopName}</Typography>
                {shop.address && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3, mb: 0.3 }}>
                        <TiLocation size={11} color={colors.textFaint} style={{ flexShrink: 0, marginTop: 1 }} />
                        <Typography sx={{ fontSize: 10, color: colors.textFaint, lineHeight: 1.3 }} noWrap>{shop.address}</Typography>
                    </Box>
                )}
                {todayHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                        <Typography sx={{ fontSize: 9 }}>🕐</Typography>
                        <Typography sx={{ fontSize: 10, color: colors.textFaint }} noWrap>{todayHours}</Typography>
                    </Box>
                )}
                {shop.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                        <FaPhoneAlt size={9} color={colors.textFaint} />
                        <Typography sx={{ fontSize: 10, color: colors.textFaint }} noWrap>{shop.phone}</Typography>
                    </Box>
                )}
                {rating > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <FaStar size={10} color={colors.warning} />
                        <Typography sx={{ fontSize: 10, color: colors.textMuted, fontWeight: 600 }}>
                            {rating.toFixed(1)}
                            <Typography component="span" sx={{ fontWeight: 400, opacity: 0.6, fontSize: 9 }}> ({reviewCount})</Typography>
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ─── Barbers Panel (inline) ───────────────────────────────────────────────────
const BarbersPanel = ({ shop, onBack, onSelectBarber, search }) => {
    const [barbers, setBarbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!shop) return;
        setLoading(true);
        const load = async () => {
            try {
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
                const snap = await getDocs(collection(db, 'shops', shop.id, 'barbers'));
                const data = await Promise.all(snap.docs.map(async (d) => {
                    const b = { id: d.id, ...d.data() };
                    if (b.isAvailable === false) return null;
                    try {
                        const q = query(collection(db, 'bookings'), where('barberId', '==', d.id), where('scheduledAt', '>=', Timestamp.fromDate(today)), where('scheduledAt', '<', Timestamp.fromDate(tomorrow)), where('status', '!=', 'cancelled'));
                        b.todayBookings = (await getDocs(q)).size;
                    } catch { b.todayBookings = 0; }
                    return b;
                }));
                setBarbers(data.filter(Boolean));
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [shop?.id]);

    // ── Filter barbers by search ──────────────────────────────────────────────
    const filteredBarbers = useMemo(() => {
        if (!search) return barbers;
        const q = search.toLowerCase();
        return barbers.filter(b =>
            b.name?.toLowerCase().includes(q) ||
            b.specialty?.some(s => s.toLowerCase().includes(q)) ||
            b.bio?.toLowerCase().includes(q)
        );
    }, [barbers, search]);

    return (
        <Box>
            {/* Shop header card */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, bgcolor: colors.primary, borderRadius: 3, p: 1.5, boxShadow: `0 4px 20px ${colors.primary}66` }}>
                <Box onClick={onBack} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#322920', flexShrink: 0 }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                </Box>
                {/* Shop image thumbnail */}
                <Box sx={{
                    width: 72, height: 72, flexShrink: 0,
                    borderRadius: 2, overflow: 'hidden',
                    bgcolor: 'rgba(50,41,32,0.15)',
                    border: '2px solid rgba(50,41,32,0.15)',
                }}>
                    <Box component="img"
                        src={shop?.image || fallbackShopImage}
                        alt={shop?.shopName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2, mb: 0.3 }} noWrap>
                        {shop?.shopName ?? 'Loading...'}
                    </Typography>
                    {shop?.address && (
                        <Typography sx={{ fontSize: 11, color: colors.textPrimary, mb: 0.2 }} noWrap>
                            📍 {shop.address}
                        </Typography>
                    )}
                    {shop?.operatingHours && (() => {
                        const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const hours = shop.operatingHours[DAY_KEYS[new Date().getDay()]];
                        if (!hours || hours.isOff) return <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>🕐 Closed today</Typography>;
                        return <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>🕐 {formatTo12(hours.open)} – {formatTo12(hours.close)}</Typography>;
                    })()}
                    {shop?.phone && (
                        <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>📞 {shop.phone}</Typography>
                    )}
                </Box>
                {/* Walk-in button */}
                <Box onClick={() => navigate(`/walkin/${shop.id}`)} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(34,197,94,0.25)', border: '1px solid rgba(34,197,94,0.5)', borderRadius: 999, px: 1.2, py: 0.5, cursor: 'pointer', transition: 'all .2s', '&:hover': { bgcolor: 'rgba(34,197,94,0.40)' } }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.success, boxShadow: `0 0 6px ${colors.success}` }} />
                    <Typography sx={{ fontSize: 10, color: colors.success, fontWeight: 700 }}>Walk In</Typography>
                </Box>

            </Box>



            {/* Section title + search context hint */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
                    Choose Your Barber
                </Typography>
                {search && (
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                        {filteredBarbers.length} result{filteredBarbers.length !== 1 ? 's' : ''} for "{search}"
                    </Typography>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={110} sx={{ bgcolor: colors.bgCard, borderRadius: 3 }} />)}
                </Box>
            ) : filteredBarbers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ fontSize: 28 }}>✂️</Typography>
                    <Typography sx={{ fontSize: 14, color: colors.textFaint, mt: 1 }}>
                        {search ? `No barbers match "${search}"` : 'No barbers available'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {filteredBarbers.map((barber) => {
                        const todayHours = getTodayHours(barber, shop);
                        const hoursLabel = formatHoursLabel(todayHours);
                        const isWorkingToday = !!todayHours;
                        const rating = barber.rating ?? barber.ratings?.average ?? 0;
                        const ratingCount = barber.ratingCount ?? barber.ratings?.totalReviews ?? 0;
                        return (
                            <Box
                                key={barber.id}
                                onClick={() => onSelectBarber(barber)}
                                sx={{
                                    display: 'flex',
                                    bgcolor: colors.bgCard,
                                    borderRadius: radius.lg,
                                    
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'border-color .2s, transform .2s',
                                    '&:hover': { borderColor: colors.primaryBorder, transform: 'translateY(-2px)' },
                                    opacity: isWorkingToday ? 1 : 0.6,

                                }}
                            >
                                {/* ── Photo ── */}
                                <Box sx={{ position: 'relative', flexShrink: 0, width: 110 }}>
                                    <Box component="img" src={barber.image || fallbackBarberImage} alt={barber.name}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block', minHeight: 130 }} />

                                    {/* Availability dot */}
                                    <Box sx={{
                                        position: 'absolute', bottom: 8, left: 8,
                                        width: 8, height: 8, borderRadius: '50%',
                                        bgcolor: isWorkingToday ? colors.success : colors.error,
                                        border: `2px solid ${colors.bgCard}`,
                                    }} />

                                    {/* Off-today badge */}
                                    {!isWorkingToday && (
                                        <Box sx={{
                                            position: 'absolute', top: 8, left: 6,
                                            bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`,
                                            borderRadius: radius.sm, px: 0.8, py: 0.2,
                                        }}>
                                            <Typography sx={{ fontSize: 9, color: colors.error, fontWeight: 600 }}>OFF TODAY</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* ── Info ── */}
                                <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                                    {/* Name + rating */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                                            {barber.name}
                                        </Typography>
                                        {rating > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                <Star sx={{ fontSize: 12, color: colors.warning }} />
                                                <Typography sx={{ fontSize: 12, color: colors.warning, fontWeight: 600 }}>{rating.toFixed(1)}</Typography>
                                                {ratingCount > 0 && (
                                                    <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>({ratingCount})</Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Experience */}
                                    {barber.experience && (
                                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>
                                            ✂️ {barber.experience} yrs experience
                                        </Typography>
                                    )}

                                    {/* Hours */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, color: isWorkingToday ? colors.textPrimary : colors.textPrimary }}>
                                            🕒 {hoursLabel}
                                        </Typography>
                                        {barber.operatingHours && (
                                            <Box sx={{ px: 0.6, py: 0.1, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.sm, }}>
                                                <Typography sx={{ fontSize: 8, color: colors.textPrimary }}>custom</Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Queue + bookings badges */}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.3,
                                            bgcolor: colors.bgDeep, px: 1, py: 0.3,
                                            borderRadius: radius.sm, border: `1px solid ${colors.textPrimary}`,
                                        }}>
                                            <MdTimer style={{ fontSize: 11, color: colors.textPrimary }} />
                                            <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>
                                                {barber.waitingList?.length ?? 0} in queue
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            px: 1, py: 0.3, borderRadius: radius.sm,
                                            bgcolor: barber.todayBookings === 0 ? colors.successBg : colors.warningBg,
                                            border: `1px solid ${barber.todayBookings === 0 ? colors.successBorder : colors.warningBorder}`,
                                        }}>
                                            <Typography sx={{ fontSize: 11, color: barber.todayBookings === 0 ? colors.success : colors.warning }}>
                                                {barber.todayBookings === 0 ? 'Free today' : `${barber.todayBookings} booked today`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Specialty chips */}
                                    {barber.specialty?.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {barber.specialty.slice(0, 3).map((s, i) => (
                                                <Box key={i} sx={{
                                                    px: 1, py: 0.2, borderRadius: radius.sm,
                                                    bgcolor: colors.primary, border: `1px solid ${colors.border}`,
                                                }}>
                                                    <Typography sx={{ fontSize: 10, color: colors.textPrimary }}>{s.trim()}</Typography>
                                                </Box>
                                            ))}
                                            {barber.specialty.length > 3 && (
                                                <Typography sx={{ fontSize: 10, color: colors.textFaint, alignSelf: 'center' }}>
                                                    +{barber.specialty.length - 3} more
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* ── Chevron ── */}
                                <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
                                    <ArrowForwardIos sx={{ fontSize: 14, color: colors.textFaintest }} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const UserDashboard = () => {
    const [allShops, setAllShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');
    const [activeTab, setActiveTab] = useState('Shops');
    const [selectedShop, setSelectedShop] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [locLoading, setLocLoading] = useState(false);
    const navigate = useNavigate();

    // ── Search placeholder changes with context ───────────────────────────────
    const searchPlaceholder = activeTab === 'Barbers' && selectedShop
        ? `Search barbers in ${selectedShop.shopName}...`
        : 'Search Shop Here...';

    useEffect(() => {
        const load = async () => {
            try {
                const shopDocs = await getDocs(collection(db, 'shops'));
                const shopsData = await Promise.all(shopDocs.docs.map(async (d) => {
                    const shopData = { id: d.id, ...d.data() };
                    const barbersSnap = await getDocs(collection(db, 'shops', d.id, 'barbers'));
                    shopData.barberCount = barbersSnap.size;
                    return shopData;
                }));
                setAllShops(shopsData);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    const handleFilter = (page) => {
        if (filter === page) { setFilter(''); return; }
        setFilter(page);
        if (page === 'Near Me') {
            setLocLoading(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocLoading(false); },
                () => { alert('Please enable location access.'); setFilter(''); setLocLoading(false); }
            );
        }
    };

    const handleShopClick = (shop) => {
        setSelectedShop(shop);
        setActiveTab('Barbers');
        setSearch(''); // clear search when switching context
    };

    const handleBackToShops = () => {
        setSelectedShop(null);
        setActiveTab('Shops');
        setSearch('');
    };

    const handleSelectBarber = (barber) => {
        navigate(`/shop/${selectedShop.id}/barber/${barber.id}/book`);
    };

    const filteredShops = useMemo(() => {
        let result = [...allShops].filter(isSubscriptionActive);
        if (search) { const q = search.toLowerCase(); result = result.filter(s => s.shopName?.toLowerCase().includes(q) || s.address?.toLowerCase().includes(q)); }
        if (filter === 'Available Now') result = result.filter(isOpenNow);
        if (filter === 'Popular') result = [...result].sort((a, b) => getShopRating(b) - getShopRating(a));
        if (filter === 'Near Me' && userLocation) {
            result = result.filter(s => s.location?.latitude && s.location?.longitude)
                .sort((a, b) => getDistance(userLocation.lat, userLocation.lng, a.location.latitude, a.location.longitude) - getDistance(userLocation.lat, userLocation.lng, b.location.latitude, b.location.longitude));
        }
        return result;
    }, [allShops, search, filter, userLocation]);

    return (
        <Box sx={{ background: colors.bgGradient, backgroundAttachment: 'fixed', minHeight: '100vh', color: colors.textPrimary, pb: 8 }}>
            <CssBaseline />

            {/* ── NavBar: placeholder and search value are context-aware ── */}
            <NavBar
                showSearch
                searchValue={search}
                onSearch={setSearch}
                placeholder={searchPlaceholder}
            />

            {/* Filter pills — Shops tab only */}
            {activeTab === 'Shops' && (
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint }}>
                        {locLoading ? 'Getting location...' : `${filteredShops.length} shop${filteredShops.length !== 1 ? 's' : ''} found`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                        {['Available Now', 'Popular', 'Near Me'].map(f => (
                            <Box key={f} onClick={() => handleFilter(f)} sx={{
                                px: 1, py: 0.3, borderRadius: 999, cursor: 'pointer',
                                bgcolor: filter === f ? colors.primary : colors.bgDeep,
                                border: `1px solid ${filter === f ? colors.primary : colors.border}`,
                                transition: 'all .15s',
                            }}>
                                <Typography sx={{ fontSize: 9, fontWeight: 600, color: filter === f ? '#322920' : colors.textFaint, whiteSpace: 'nowrap' }}>{f}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {filter && activeTab === 'Shops' && (
                <Box sx={{ px: 3, mb: 1 }}>
                    <Box onClick={() => setFilter('')} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: colors.primaryDim, border: `1px solid ${colors.primary}`, borderRadius: 999, px: 1.5, py: 0.3, cursor: 'pointer' }}>
                        <Typography sx={{ fontSize: 11, color: colors.textPrimary, fontWeight: 600 }}>{filter} ✕</Typography>
                    </Box>
                </Box>
            )}

            {/* ── Tab bar ── */}
            <Box sx={{ display: 'flex', px: 2, pt: 0.5, borderBottom: `2px solid ${colors.bg}` }}>
                {TABS.map(tab => {
                    const isActive = activeTab === tab;
                    return (
                        <Box key={tab} onClick={() => {
                            setActiveTab(tab);
                            if (tab !== 'Barbers') { setSelectedShop(null); setSearch(''); }
                        }} sx={{
                            px: 2.5, py: 1, cursor: 'pointer',
                            bgcolor: isActive ? colors.bg : 'transparent',
                            borderRadius: '12px 12px 0 0',
                            border: isActive ? `1px solid ${colors.border}` : '1px solid transparent',
                            borderBottom: isActive ? `2px solid ${colors.bg}` : '1px solid transparent',
                            mb: isActive ? '-2px' : 0,
                            zIndex: isActive ? 1 : 0,
                            transition: 'all .2s',
                        }}>
                            <Typography sx={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? colors.textPrimary : colors.textFaint, whiteSpace: 'nowrap' }}>
                                {tab}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* ── Content area ── */}
            <Box sx={{ bgcolor: colors.bg, minHeight: '100vh', pt: 2, px: 3 }}>

                {/* Shops tab */}
                {activeTab === 'Shops' && (
                    <>
                        {loading && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={220} sx={{ bgcolor: colors.bgCard, borderRadius: 3 }} />)}
                            </Box>
                        )}
                        {!loading && filteredShops.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography sx={{ fontSize: 32 }}>✂️</Typography>
                                <Typography sx={{ fontSize: 15, color: colors.textFaint, mt: 1 }}>
                                    {filter === 'Available Now' ? 'No shops open right now' :
                                        filter === 'Near Me' ? 'No shops nearby' :
                                            filter === 'Popular' ? 'No rated shops yet' : 'No shops found'}
                                </Typography>
                                {(filter || search) && (
                                    <Typography onClick={() => { setFilter(''); setSearch(''); }} sx={{ fontSize: 13, color: colors.primary, mt: 1, cursor: 'pointer' }}>Clear filters</Typography>
                                )}
                            </Box>
                        )}
                        {!loading && filteredShops.length > 0 && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 ,pb:2}}>
                                {filteredShops.map((shop) => (
                                    <ShopGridCard key={shop.id} shop={shop} onClick={() => handleShopClick(shop)} />
                                ))}
                            </Box>
                        )}
                    </>
                )}

                {/* Barbers tab */}
                {activeTab === 'Barbers' && (
                    selectedShop ? (
                        // ── Pass search down so BarbersPanel can filter ──
                        <BarbersPanel
                            shop={selectedShop}
                            onBack={handleBackToShops}
                            onSelectBarber={handleSelectBarber}
                            search={search}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ fontSize: 32 }}>✂️</Typography>
                            <Typography sx={{ fontSize: 15, color: colors.textFaint, mt: 1 }}>Tap a shop to see its barbers</Typography>
                            <Box onClick={() => setActiveTab('Shops')} sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: colors.primary, borderRadius: 999, px: 2, py: 0.8, cursor: 'pointer' }}>
                                <Typography sx={{ fontSize: 13, color: '#322920', fontWeight: 600 }}>Browse Shops</Typography>
                            </Box>
                        </Box>
                    )
                )}

                {/* My Queue tab */}
                {activeTab === 'My Queue' && (
                    <Box sx={{ py: 2 }}>
                        <UserBookings />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default UserDashboard;